// src/components/IndexCorrelationLab.js
//
// Gamified cross-correlation discovery lab. User picks an anchor asset
// (^BVSP, ^GSPC, etc.), then toggles overlays (commodities, FX, rates,
// other indices, ETF wrappers). Every series is base-100 normalized so
// they're visually comparable. Pairwise Pearson correlation is computed
// live on monthly returns.
//
// STRICT DATA LAYER
// ─────────────────
// Tickers are sent to :6062/api/history EXACTLY as declared. No client-
// side .SA guessing, no fuzzy suffix logic. The backend has aliases +
// tickerInfo maps and is the single source of truth for symbol resolution.
//
// Tier 3 (:8080) serves MONTHLY OHLC, not daily — historical bars land
// on first-of-month, the in-progress bar uses today's date. Two tickers
// fetched seconds apart can therefore have "today" candles on different
// dates. To align robustly across markets, every candle is collapsed to
// its YYYY-MM month-key before comparison. This is the same grid the
// backend uses internally for its Prometheus counters.
//
// Empty results DO NOT poison the cache. A ticker that returned zero
// candles or an error is tracked in `failedTickers` for the current
// session — the UI can warn the user — but the ticker will be RE-FETCHED
// on the next batch call so transient yfinance flakes heal on interaction.
//
// Visual grammar borrowed from ThesisCenariosBlock: black gradient bg,
// subtle grid, IBM Plex Mono for data, Fraunces for headlines, gold
// #D4AF37 for editorial accents, purple #6660fe for Magma CTAs.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LineChart, Line,
  XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine,
} from 'recharts';

// ─── ENDPOINTS ──────────────────────────────────────────────
const INDICES_API = '/api/history';

// ─── PALETTE ────────────────────────────────────────────────
const C = {
  bg: '#0a0a0a', card: '#141414',
  border: '#1e1e1e', borderSoft: '#181818',
  gold: '#D4AF37', purple: '#6660fe',
  green: '#4ade80', red: '#ef4444',
  amber: '#fbbf24', cyan: '#22d3ee',
  white: '#e8e8f0',
  dim1: 'rgba(255,255,255,0.5)',
  dim2: 'rgba(255,255,255,0.35)',
  dim3: 'rgba(255,255,255,0.2)',
  dim4: 'rgba(255,255,255,0.08)',
};

const FONT_IMPORT = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600;700&family=Fraunces:ital,wght@0,600;0,700;1,600&display=swap');
`;

// ─── ANCHORS ────────────────────────────────────────────────
const ANCHORS = [
  { ticker: '^BVSP', label: 'IBOV',      full: 'IBOVESPA',   flag: 'br' },
  { ticker: '^GSPC', label: 'S&P',       full: 'S&P 500',    flag: 'us' },
  { ticker: '^NDX',  label: 'NASDAQ',    full: 'Nasdaq 100', flag: 'us' },
  { ticker: '^N225', label: 'NIKKEI',    full: 'Nikkei 225', flag: 'jp' },
  { ticker: '^GDAXI',label: 'DAX',       full: 'DAX',        flag: 'de' },
  { ticker: '^HSI',  label: 'HSI',       full: 'Hang Seng',  flag: 'hk' },
];

// ─── OVERLAYS ───────────────────────────────────────────────
const OVERLAY_GROUPS = [
  {
    id: 'fx',
    label: 'FX',
    items: [
      { ticker: 'BRL=X',    label: 'USD/BRL',  color: '#f472b6' },
      { ticker: 'EURBRL=X', label: 'EUR/BRL',  color: '#fb7185' },
      { ticker: 'DX-Y.NYB', label: 'DXY',      color: '#c084fc' },
      { ticker: 'JPY=X',    label: 'USD/JPY',  color: '#a78bfa' },
    ],
  },
  {
    id: 'commodities',
    label: 'Commodities',
    items: [
      { ticker: 'GC=F', label: 'Ouro',       color: '#fbbf24' },
      { ticker: 'CL=F', label: 'WTI',        color: '#fb923c' },
      { ticker: 'BZ=F', label: 'Brent',      color: '#f97316' },
      { ticker: 'HG=F', label: 'Cobre',      color: '#ea580c' },
      { ticker: 'SI=F', label: 'Prata',      color: '#94a3b8' },
      { ticker: 'ZS=F', label: 'Soja',       color: '#84cc16' },
      { ticker: 'KC=F', label: 'Café',       color: '#78350f' },
    ],
  },
  {
    id: 'rates',
    label: 'Juros / Medo',
    items: [
      { ticker: '^TNX', label: 'US 10Y',   color: '#38bdf8' },
      { ticker: '^FVX', label: 'US 5Y',    color: '#0ea5e9' },
      { ticker: '^VIX', label: 'VIX',      color: '#ef4444' },
    ],
  },
  {
    id: 'indices',
    label: 'Índices',
    items: [
      { ticker: '^BVSP',    label: 'IBOV',    color: '#22d3ee' },
      { ticker: '^GSPC',    label: 'S&P',     color: '#60a5fa' },
      { ticker: '^NDX',     label: 'NASDAQ',  color: '#a78bfa' },
      { ticker: '^N225',    label: 'NIKKEI',  color: '#f472b6' },
      { ticker: '^GDAXI',   label: 'DAX',     color: '#facc15' },
      { ticker: '^STOXX50E',label: 'EU50',    color: '#e879f9' },
      { ticker: '^HSI',     label: 'HSI',     color: '#f87171' },
    ],
  },
  {
    id: 'etfs',
    label: 'ETFs',
    items: [
      { ticker: 'EWZ', label: 'EWZ · Brazil', color: '#22c55e' },
      { ticker: 'SPY', label: 'SPY · S&P',    color: '#3b82f6' },
      { ticker: 'QQQ', label: 'QQQ · Nasdaq', color: '#8b5cf6' },
      { ticker: 'FXI', label: 'FXI · China',  color: '#ef4444' },
      { ticker: 'INDA',label: 'INDA · India', color: '#f97316' },
    ],
  },
];

const ALL_OVERLAYS = OVERLAY_GROUPS.flatMap(g => g.items);

// ─── TIME RANGES (in months) ────────────────────────────────
const RANGES = [
  { id: '6m',  label: '6M',  months: 6   },
  { id: '1y',  label: '1A',  months: 12  },
  { id: '3y',  label: '3A',  months: 36  },
  { id: '5y',  label: '5A',  months: 60  },
  { id: '10y', label: '10A', months: 120 },
];

// ─── ACHIEVEMENTS ───────────────────────────────────────────
const ACHIEVEMENTS = [
  {
    id: 'first_overlay',
    icon: '🎲',
    name: 'Primeira Descoberta',
    desc: 'Adicionou o primeiro overlay ao anchor.',
    check: (s) => s.usedOverlays.size >= 1,
  },
  {
    id: 'explorer_5',
    icon: '🌍',
    name: 'Explorador',
    desc: 'Testou 5 overlays diferentes na sessão.',
    check: (s) => s.usedOverlays.size >= 5,
  },
  {
    id: 'explorer_10',
    icon: '🎓',
    name: 'Autodidata',
    desc: 'Testou 10 overlays diferentes na sessão.',
    check: (s) => s.usedOverlays.size >= 10,
  },
  {
    id: 'anchor_hopper',
    icon: '🧭',
    name: 'Trocou de Âncora',
    desc: 'Usou 3 anchors diferentes.',
    check: (s) => s.usedAnchors.size >= 3,
  },
  {
    id: 'mirror_move',
    icon: '🎯',
    name: 'Movimento Espelhado',
    desc: 'Encontrou uma correlação > 0.7 (mesmo sentido).',
    check: (s) => Object.values(s.correlations).some(c => c > 0.7),
  },
  {
    id: 'inverse_mirror',
    icon: '🔄',
    name: 'Espelho Inverso',
    desc: 'Encontrou uma correlação < -0.5 (sentido oposto).',
    check: (s) => Object.values(s.correlations).some(c => c < -0.5),
  },
  {
    id: 'ambiguous',
    icon: '🌗',
    name: 'Território Ambíguo',
    desc: 'Descobriu um par com correlação entre 0.3 e 0.5.',
    check: (s) => Object.values(s.correlations).some(c => c > 0.3 && c < 0.5),
  },
  {
    id: 'triple_hunter',
    icon: '🔥',
    name: 'Caçador de Padrões',
    desc: '3 correlações fortes descobertas na sessão.',
    check: (s) => s.strongCount >= 3,
  },
  {
    id: 'long_horizon',
    icon: '📜',
    name: 'Longo Prazo',
    desc: 'Investigou 5 anos ou mais de história.',
    check: (s) => s.usedRanges.has('5y') || s.usedRanges.has('10y'),
  },
  {
    id: 'macro_alchemist',
    icon: '🥇',
    name: 'Alquimista Macro',
    desc: 'Cruzou índice × commodity × FX na mesma tela.',
    check: (s) => s.usedCategories.has('commodities') && s.usedCategories.has('fx'),
  },
];

// ─── STORAGE ────────────────────────────────────────────────
const UNLOCKED_KEY = 'rr_corr_lab_unlocked_v1';

function loadUnlocked() {
  try { return new Set(JSON.parse(localStorage.getItem(UNLOCKED_KEY) || '[]')); }
  catch { return new Set(); }
}
function saveUnlocked(set) {
  try { localStorage.setItem(UNLOCKED_KEY, JSON.stringify([...set])); }
  catch { /* noop */ }
}

// ═══════════════════════════════════════════════════════════
// STRICT DATA LAYER
// ═══════════════════════════════════════════════════════════

// Session cache. Key = ticker EXACTLY as declared. Value = monthly
// series [{ ym: "2026-07", close }, ...] ascending. Empty results
// do NOT get cached — see failedTickers below.
const historyCache = new Map();

// Tickers whose last fetch attempt returned nothing or errored. Tracked
// so the UI can warn the user; NOT used to skip the ticker on retry.
const failedTickers = new Set();

// Collapse raw candles to at most one point per calendar month.
// The in-progress month's candle (today's date) wins because it's the
// last one written for its YYYY-MM key.
function toMonthly(candles) {
  if (!Array.isArray(candles) || !candles.length) return [];
  const byMonth = new Map();
  for (const c of candles) {
    if (!c || c.close == null || c.date == null) continue;
    const ym = String(c.date).slice(0, 7);
    if (ym.length !== 7) continue;
    byMonth.set(ym, c.close);
  }
  return Array.from(byMonth.entries())
    .sort((a, b) => (a[0] < b[0] ? -1 : 1))
    .map(([ym, close]) => ({ ym, close }));
}

async function fetchHistoryBatch(tickers) {
  const unique = Array.from(new Set(tickers.filter(Boolean)));
  const need = unique.filter(t => !historyCache.has(t));

  if (need.length > 0) {
    try {
      const url = `${INDICES_API}/api/history?tickers=${encodeURIComponent(need.join(','))}`;
      const r = await fetch(url);
      if (!r.ok) throw new Error(`http ${r.status}`);
      const j = await r.json();
      const series = j.series || {};
      const missing = new Set(j.missing || []);

      for (const t of need) {
        const entry = series[t];
        const candles = entry && Array.isArray(entry.candles) ? entry.candles : [];
        const monthly = toMonthly(candles);

        if (missing.has(t) || monthly.length === 0 || entry?.error) {
          failedTickers.add(t);
        } else {
          historyCache.set(t, monthly);
          failedTickers.delete(t);
        }
      }
    } catch (e) {
      console.warn('[CorrelationLab] batch fetch:', e);
      for (const t of need) failedTickers.add(t);
    }
  }

  const result = {};
  for (const t of unique) result[t] = historyCache.get(t) || [];
  return result;
}

// ─── MATH ───────────────────────────────────────────────────
function filterMonths(series, months) {
  if (!series.length || !months) return series;
  const now = new Date();
  const cutoff = new Date(now.getFullYear(), now.getMonth() - months + 1, 1);
  const cy = cutoff.getFullYear();
  const cm = String(cutoff.getMonth() + 1).padStart(2, '0');
  const cutoffYm = `${cy}-${cm}`;
  return series.filter(p => p.ym >= cutoffYm);
}

function toReturns(closes) {
  const r = [];
  for (let i = 1; i < closes.length; i++) {
    const prev = closes[i - 1];
    r.push(prev === 0 ? 0 : (closes[i] - prev) / prev);
  }
  return r;
}

function pearson(x, y) {
  const n = Math.min(x.length, y.length);
  if (n < 5) return null;
  let sx = 0, sy = 0;
  for (let i = 0; i < n; i++) { sx += x[i]; sy += y[i]; }
  const mx = sx / n, my = sy / n;
  let num = 0, dx2 = 0, dy2 = 0;
  for (let i = 0; i < n; i++) {
    const dx = x[i] - mx, dy = y[i] - my;
    num += dx * dy; dx2 += dx * dx; dy2 += dy * dy;
  }
  const den = Math.sqrt(dx2 * dy2);
  return den === 0 ? null : num / den;
}

function corrStrength(c) {
  const a = Math.abs(c);
  if (a >= 0.7) return { label: 'FORTE',           color: C.green };
  if (a >= 0.5) return { label: 'MODERADA',        color: C.amber };
  if (a >= 0.3) return { label: 'FRACA',           color: C.cyan  };
  return          { label: 'INSIGNIFICANTE',       color: C.dim2  };
}

// ─── ACHIEVEMENT TOAST ─────────────────────────────────────
const AchievementToast = ({ achievement, onDismiss }) => {
  useEffect(() => {
    const t = setTimeout(onDismiss, 4500);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
      background: 'linear-gradient(135deg, rgba(20,20,20,0.98), rgba(30,30,30,0.98))',
      border: '1px solid rgba(212,175,55,0.5)',
      borderLeft: `4px solid ${C.gold}`,
      borderRadius: 8,
      padding: '14px 18px',
      minWidth: 300, maxWidth: 380,
      boxShadow: '0 12px 40px rgba(0,0,0,0.6), 0 0 30px rgba(212,175,55,0.2)',
      display: 'flex', gap: 12, alignItems: 'flex-start',
      animation: 'rr-toast-slide 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
      fontFamily: "'IBM Plex Mono', monospace",
    }}>
      <div style={{ fontSize: 32, lineHeight: 1, flexShrink: 0 }}>{achievement.icon}</div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{
          fontSize: 8, letterSpacing: '0.18em', fontWeight: 700,
          color: C.gold, marginBottom: 4,
        }}>DESBLOQUEADO · ACHIEVEMENT</div>
        <div style={{
          fontFamily: "'Fraunces', Georgia, serif",
          fontSize: 15, fontWeight: 700, color: C.white,
          marginBottom: 4, letterSpacing: '-0.01em',
        }}>{achievement.name}</div>
        <div style={{
          fontSize: 10.5, color: C.dim1, lineHeight: 1.4,
        }}>{achievement.desc}</div>
      </div>
      <button onClick={onDismiss} style={{
        background: 'transparent', border: 'none', color: C.dim2,
        cursor: 'pointer', fontSize: 14, padding: 0, flexShrink: 0,
      }}>✕</button>
    </div>
  );
};

// ─── OVERLAY PILL ───────────────────────────────────────────
const OverlayPill = ({ item, active, disabled, failed, onToggle }) => (
  <button
    onClick={onToggle}
    disabled={disabled}
    title={failed ? `${item.ticker}: sem dado no :6062` : undefined}
    style={{
      padding: '5px 11px',
      borderRadius: 4,
      border: `1px solid ${active ? item.color + '80' : C.border}`,
      background: active ? `${item.color}15` : (disabled ? 'transparent' : 'rgba(255,255,255,0.02)'),
      color: active ? item.color : (disabled ? C.dim3 : C.dim1),
      fontSize: 10, fontWeight: 700,
      fontFamily: "'IBM Plex Mono', monospace",
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.4 : (active && failed ? 0.55 : 1),
      letterSpacing: '0.02em',
      whiteSpace: 'nowrap',
      display: 'inline-flex', alignItems: 'center', gap: 6,
      transition: 'all 0.15s',
    }}
  >
    <span style={{
      width: 7, height: 7, borderRadius: '50%',
      background: active ? item.color : C.dim3,
    }} />
    {item.label}
    {active && failed && <span style={{ fontSize: 8, color: C.red }}>✕</span>}
  </button>
);

// ─── MAIN COMPONENT ─────────────────────────────────────────
export const IndexCorrelationLab = () => {
  const [anchorTicker, setAnchorTicker] = useState('^BVSP');
  const [activeOverlays, setActiveOverlays] = useState(['GC=F', 'BRL=X']);
  const [rangeId, setRangeId] = useState('3y');
  const [seriesCache, setSeriesCache] = useState({});
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [tickerStatus, setTickerStatus] = useState({}); // ticker → 'ok'|'failed'

  // Session-scoped discovery state
  const [usedOverlays] = useState(() => new Set(['GC=F', 'BRL=X']));
  const [usedAnchors]  = useState(() => new Set(['^BVSP']));
  const [usedRanges]   = useState(() => new Set(['3y']));
  const [usedCategories] = useState(() => new Set(['commodities', 'fx']));
  const strongCountRef = useRef(0);
  const [strongCount, setStrongCount] = useState(0);

  const [unlocked, setUnlocked] = useState(loadUnlocked);
  const toastQueue = useRef([]);

  const anchor = ANCHORS.find(a => a.ticker === anchorTicker) || ANCHORS[0];
  const range = RANGES.find(r => r.id === rangeId) || RANGES[2];

  // ── Batch fetch on any change ──
  useEffect(() => {
    let alive = true;
    setLoading(true);
    const needed = [anchorTicker, ...activeOverlays];
    fetchHistoryBatch(needed).then(map => {
      if (!alive) return;
      setSeriesCache(prev => ({ ...prev, ...map }));
      const status = {};
      for (const t of needed) {
        status[t] = failedTickers.has(t) ? 'failed' : (map[t]?.length ? 'ok' : 'failed');
      }
      setTickerStatus(prev => ({ ...prev, ...status }));
      setLoading(false);
    });
    return () => { alive = false; };
  }, [anchorTicker, activeOverlays]);

  // ── Build chart data on monthly grid ──
  const { chartData, correlations, returnsPct } = useMemo(() => {
    const anchorRaw = seriesCache[anchorTicker] || [];
    if (!anchorRaw.length) {
      return { chartData: [], correlations: {}, returnsPct: {} };
    }

    // Trim each series to range window.
    const anchorTrim = filterMonths(anchorRaw, range.months);
    if (anchorTrim.length < 2) {
      return { chartData: [], correlations: {}, returnsPct: {} };
    }

    // Union of all months across active overlays (already filtered).
    const overlayTrim = {};
    for (const ovT of activeOverlays) {
      const s = seriesCache[ovT];
      if (!s || !s.length) continue;
      const trim = filterMonths(s, range.months);
      if (trim.length >= 2) overlayTrim[ovT] = trim;
    }

    // Combine month keys — anchor determines the x-axis, but we include
    // overlay months so no data is silently dropped.
    const monthSet = new Set(anchorTrim.map(p => p.ym));
    Object.values(overlayTrim).forEach(s => s.forEach(p => monthSet.add(p.ym)));
    const months = Array.from(monthSet).sort();

    // Build maps for O(1) lookup.
    const anchorMap = new Map(anchorTrim.map(p => [p.ym, p.close]));
    const overlayMaps = {};
    for (const [ovT, s] of Object.entries(overlayTrim)) {
      overlayMaps[ovT] = new Map(s.map(p => [p.ym, p.close]));
    }

    // Base value = first month where the series has data.
    const anchorFirst = anchorTrim[0].close;
    const anchorLast = anchorTrim[anchorTrim.length - 1].close;

    const rPct = {};
    rPct[anchorTicker] = ((anchorLast / anchorFirst) - 1) * 100;

    const rows = months.map(ym => {
      const row = { ym };
      const av = anchorMap.get(ym);
      if (av != null) row.anchor = (av / anchorFirst) * 100;
      for (const [ovT, om] of Object.entries(overlayMaps)) {
        const v = om.get(ym);
        if (v != null) {
          const trim = overlayTrim[ovT];
          const base = trim[0].close;
          row[ovT] = (v / base) * 100;
        }
      }
      return row;
    });

    // Correlation on aligned month returns.
    const corrs = {};
    for (const [ovT, om] of Object.entries(overlayMaps)) {
      const alignedAnchor = [];
      const alignedOverlay = [];
      months.forEach(ym => {
        const av = anchorMap.get(ym);
        const ov = om.get(ym);
        if (av != null && ov != null) {
          alignedAnchor.push(av);
          alignedOverlay.push(ov);
        }
      });
      if (alignedAnchor.length >= 6) {
        const corr = pearson(toReturns(alignedAnchor), toReturns(alignedOverlay));
        if (corr != null) corrs[ovT] = corr;
      }
      const trim = overlayTrim[ovT];
      rPct[ovT] = ((trim[trim.length - 1].close / trim[0].close) - 1) * 100;
    }

    return { chartData: rows, correlations: corrs, returnsPct: rPct };
  }, [seriesCache, anchorTicker, activeOverlays, range.months]);

  // Strong-correlation counter
  useEffect(() => {
    const strongThis = Object.values(correlations).filter(c => Math.abs(c) > 0.7).length;
    if (strongThis > 0) {
      strongCountRef.current += strongThis;
      setStrongCount(strongCountRef.current);
    }
  }, [correlations]);

  // Achievement checker
  useEffect(() => {
    const state = {
      usedOverlays, usedAnchors, usedRanges, usedCategories,
      correlations, strongCount,
    };
    ACHIEVEMENTS.forEach(a => {
      if (unlocked.has(a.id)) return;
      if (a.check(state)) {
        setUnlocked(prev => {
          const next = new Set(prev);
          next.add(a.id);
          saveUnlocked(next);
          return next;
        });
        toastQueue.current.push(a);
      }
    });
    if (toastQueue.current.length > 0 && !toast) {
      setToast(toastQueue.current.shift());
    }
  }, [correlations, strongCount, usedOverlays, usedAnchors, usedRanges, usedCategories, unlocked, toast]);

  const dismissToast = useCallback(() => {
    setToast(toastQueue.current.length > 0 ? toastQueue.current.shift() : null);
  }, []);

  const toggleOverlay = (ticker) => {
    setActiveOverlays(prev => {
      const has = prev.includes(ticker);
      const next = has ? prev.filter(t => t !== ticker) : [...prev, ticker];
      if (!has) {
        usedOverlays.add(ticker);
        for (const g of OVERLAY_GROUPS) {
          if (g.items.some(i => i.ticker === ticker)) {
            usedCategories.add(g.id);
            break;
          }
        }
      }
      return next;
    });
  };

  const pickAnchor = (ticker) => {
    setAnchorTicker(ticker);
    usedAnchors.add(ticker);
  };

  const pickRange = (id) => {
    setRangeId(id);
    usedRanges.add(id);
  };

  const hint = useMemo(() => {
    if (activeOverlays.length === 0) return '💡 Adicione um overlay pra começar. Comece com Ouro ou USD/BRL.';
    if (activeOverlays.length === 1) return '💡 Tente um segundo overlay de outra categoria pra cruzar sinais.';
    const cats = new Set();
    activeOverlays.forEach(t => {
      for (const g of OVERLAY_GROUPS) {
        if (g.items.some(i => i.ticker === t)) { cats.add(g.id); break; }
      }
    });
    if (!cats.has('commodities')) return '💡 Ainda não testou commodities. Ouro e Cobre costumam revelar padrões macro.';
    if (!cats.has('rates')) return '💡 Adicione VIX ou US 10Y pra ver a relação com o medo/juros globais.';
    return null;
  }, [activeOverlays]);

  const anchorFailed = tickerStatus[anchorTicker] === 'failed';

  return (
    <div style={{
      background: 'linear-gradient(180deg, #0a0a0a 0%, #141414 100%)',
      border: `1px solid ${C.border}`,
      borderRadius: 14,
      overflow: 'hidden',
      position: 'relative',
      fontFamily: "'IBM Plex Mono', monospace",
      color: C.white,
    }}>
      <style>{FONT_IMPORT}{`
        @keyframes rr-toast-slide {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .rr-corr-btn:hover { background: rgba(255,255,255,0.06) !important; }
      `}</style>

      {/* Grid backdrop */}
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.4,
        backgroundImage: `linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)`,
        backgroundSize: '32px 32px', pointerEvents: 'none',
      }} />

      {/* ── HEADER ── */}
      <div style={{ padding: '18px 24px 14px', borderBottom: `1px solid ${C.border}`, position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11, fontWeight: 800,
              color: C.purple,
              background: 'rgba(102,96,254,0.15)',
              border: '1px solid rgba(102,96,254,0.4)',
              padding: '4px 10px', borderRadius: 3,
              letterSpacing: '0.18em',
            }}>RR MAGMA</span>
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 14, fontWeight: 800, color: C.white,
              letterSpacing: '0.08em',
            }}>LABORATÓRIO · CORRELAÇÕES</span>
            <span style={{
              background: 'rgba(212,175,55,0.12)', color: C.gold,
              padding: '3px 10px', borderRadius: 3,
              fontSize: 9, fontWeight: 700, letterSpacing: '0.14em',
            }}>CROSS-ASSET DISCOVERY</span>
          </div>

          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '5px 12px',
            background: 'rgba(212,175,55,0.08)',
            border: '1px solid rgba(212,175,55,0.25)',
            borderRadius: 4,
          }}>
            <span style={{ fontSize: 14 }}>🏆</span>
            <span style={{
              fontSize: 10, letterSpacing: '0.12em', color: C.gold,
              fontWeight: 700,
            }}>
              {unlocked.size} / {ACHIEVEMENTS.length}
            </span>
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <h2 style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: 26, fontWeight: 700, margin: 0, letterSpacing: '-0.015em',
            color: C.white, lineHeight: 1.15,
          }}>
            {anchor.full} <span style={{ color: C.dim2 }}>×</span> {activeOverlays.length} indicador{activeOverlays.length !== 1 ? 'es' : ''}
          </h2>
          <p style={{
            fontFamily: "'Fraunces', Georgia, serif", fontStyle: 'italic',
            fontSize: 13, color: C.dim1, margin: '6px 0 0', lineHeight: 1.45,
          }}>
            Cruze qualquer ativo com commodities, FX, juros ou outros índices.
            Correlações fortes viram achievements. Desbloqueie os {ACHIEVEMENTS.length} padrões escondidos.
          </p>
        </div>
      </div>

      {/* ── ANCHOR PICKER ── */}
      <div style={{
        padding: '10px 24px',
        borderBottom: `1px solid ${C.border}`,
        display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap',
        position: 'relative',
      }}>
        <span style={{
          fontSize: 9, letterSpacing: '0.14em', color: C.dim3,
          fontWeight: 700, marginRight: 4,
        }}>ÂNCORA:</span>
        {ANCHORS.map(a => {
          const isActive = a.ticker === anchorTicker;
          const isFailed = tickerStatus[a.ticker] === 'failed';
          return (
            <button
              key={a.ticker}
              onClick={() => pickAnchor(a.ticker)}
              className="rr-corr-btn"
              title={isFailed ? `${a.ticker}: sem dado no :6062` : undefined}
              style={{
                padding: '6px 12px', borderRadius: 5,
                border: isActive ? `1px solid ${C.gold}66` : `1px solid ${C.border}`,
                background: isActive ? 'rgba(212,175,55,0.10)' : 'transparent',
                color: isActive ? C.gold : C.dim1,
                fontSize: 10.5, fontWeight: 700,
                fontFamily: 'inherit', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
                transition: 'all 0.15s',
                opacity: isActive && isFailed ? 0.6 : 1,
              }}
            >
              <img
                src={`https://flagcdn.com/w20/${a.flag}.png`}
                alt=""
                style={{ width: 14, height: 'auto', borderRadius: 1 }}
              />
              {a.label}
              {isActive && isFailed && <span style={{ fontSize: 9, color: C.red }}>✕</span>}
            </button>
          );
        })}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          <span style={{
            fontSize: 9, letterSpacing: '0.14em', color: C.dim3,
            fontWeight: 700, alignSelf: 'center', marginRight: 6,
          }}>JANELA:</span>
          {RANGES.map(r => {
            const isActive = r.id === rangeId;
            return (
              <button
                key={r.id}
                onClick={() => pickRange(r.id)}
                className="rr-corr-btn"
                style={{
                  padding: '5px 10px', borderRadius: 4,
                  border: isActive ? '1px solid rgba(255,255,255,0.25)' : `1px solid ${C.border}`,
                  background: isActive ? 'rgba(255,255,255,0.04)' : 'transparent',
                  color: isActive ? C.white : C.dim2,
                  fontSize: 10, fontWeight: 700,
                  fontFamily: 'inherit', cursor: 'pointer',
                  letterSpacing: '0.06em',
                }}
              >{r.label}</button>
            );
          })}
        </div>
      </div>

      {/* ── OVERLAY PICKER ── */}
      <div style={{
        padding: '12px 24px',
        borderBottom: `1px solid ${C.border}`,
        position: 'relative',
      }}>
        {OVERLAY_GROUPS.map(g => (
          <div key={g.id} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            marginBottom: 8, flexWrap: 'wrap',
          }}>
            <span style={{
              minWidth: 90,
              fontSize: 8.5, letterSpacing: '0.14em', color: C.dim3,
              fontWeight: 700,
            }}>{g.label.toUpperCase()}</span>
            {g.items
              .filter(i => i.ticker !== anchorTicker)
              .map(item => (
                <OverlayPill
                  key={item.ticker}
                  item={item}
                  active={activeOverlays.includes(item.ticker)}
                  failed={tickerStatus[item.ticker] === 'failed'}
                  onToggle={() => toggleOverlay(item.ticker)}
                />
              ))}
          </div>
        ))}
        {hint && (
          <div style={{
            marginTop: 6, padding: '6px 12px',
            background: 'rgba(102,96,254,0.06)',
            border: '1px dashed rgba(102,96,254,0.25)',
            borderRadius: 4,
            fontFamily: "'Fraunces', Georgia, serif", fontStyle: 'italic',
            fontSize: 11, color: 'rgba(160,155,240,0.85)',
          }}>{hint}</div>
        )}
      </div>

      {/* ── CHART ── */}
      <div style={{ padding: '10px 6px 0', position: 'relative' }}>
        {loading ? (
          <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 10, color: C.dim3 }}>buscando OHLCV mensal em :6062...</span>
          </div>
        ) : anchorFailed ? (
          <div style={{ height: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, color: C.red }}>âncora {anchorTicker} sem dado</span>
            <span style={{ fontSize: 10, color: C.dim2, fontStyle: 'italic', fontFamily: "'Fraunces', Georgia, serif" }}>
              troque de âncora ou aguarde o :6062 recuperar o ticker
            </span>
          </div>
        ) : chartData.length === 0 ? (
          <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 11, color: C.dim2 }}>sem dados para o período selecionado</span>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 8, right: 24, left: 4, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" vertical={false} />
              <XAxis
                dataKey="ym"
                stroke="#444" fontSize={8.5}
                tickLine={false} axisLine={false}
                interval="preserveStartEnd" minTickGap={60}
              />
              <YAxis
                stroke="#444" fontSize={8.5}
                tickLine={false} axisLine={false}
                domain={['dataMin - 5', 'dataMax + 5']}
                tickFormatter={(v) => v.toFixed(0)}
                width={36}
              />
              <ReferenceLine
                y={100}
                stroke="rgba(255,255,255,0.15)"
                strokeDasharray="4 4"
                label={{ value: 'base 100', fill: 'rgba(255,255,255,0.25)', fontSize: 8, position: 'insideTopRight' }}
              />
              <Tooltip
                contentStyle={{
                  background: 'rgba(10,10,10,0.96)',
                  border: '1px solid #2a2a2a',
                  borderRadius: 6, fontSize: 10,
                  fontFamily: "'IBM Plex Mono', monospace",
                }}
                labelStyle={{ color: 'rgba(255,255,255,0.5)' }}
                formatter={(v, name) => {
                  const overlay = ALL_OVERLAYS.find(o => o.ticker === name);
                  const label = name === 'anchor' ? anchor.label : (overlay?.label || name);
                  return [v?.toFixed(2), label];
                }}
              />
              <Line
                type="monotone" dataKey="anchor"
                stroke={C.gold} strokeWidth={2.5}
                dot={false} connectNulls
                activeDot={{ r: 4, fill: C.gold, stroke: '#000', strokeWidth: 2 }}
              />
              {activeOverlays.map(t => {
                const item = ALL_OVERLAYS.find(o => o.ticker === t);
                if (!item) return null;
                return (
                  <Line
                    key={t}
                    type="monotone" dataKey={t}
                    stroke={item.color} strokeWidth={1.5}
                    strokeDasharray="5 3"
                    dot={false} connectNulls
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ── CORRELATION TABLE ── */}
      <div style={{ margin: '0 24px', padding: '14px 0', borderTop: `1px solid ${C.border}` }}>
        <div style={{
          fontSize: 9, fontWeight: 700, color: C.dim3,
          letterSpacing: '0.14em', marginBottom: 10,
        }}>CORRELAÇÃO PEARSON · RETORNOS MENSAIS · JANELA {range.label} · {chartData.length} meses</div>

        {activeOverlays.length === 0 ? (
          <div style={{
            padding: '20px', textAlign: 'center', color: C.dim2,
            fontFamily: "'Fraunces', Georgia, serif", fontStyle: 'italic',
            fontSize: 11,
          }}>Adicione overlays para calcular correlações.</div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 100px 100px 100px',
            gap: 4,
            padding: '6px 10px',
            background: 'rgba(255,255,255,0.02)', borderRadius: 4, marginBottom: 3,
          }}>
            {['OVERLAY', 'CORREL', 'FORÇA', `RET ${range.label}`].map(h => (
              <span key={h} style={{
                fontSize: 8, fontWeight: 700, color: C.dim3, letterSpacing: '0.12em',
              }}>{h}</span>
            ))}
          </div>
        )}

        {activeOverlays.map((t, i) => {
          const item = ALL_OVERLAYS.find(o => o.ticker === t);
          if (!item) return null;
          const corr = correlations[t];
          const ret = returnsPct[t];
          const strength = corr != null ? corrStrength(corr) : null;
          const isFailed = tickerStatus[t] === 'failed';
          return (
            <div key={t} style={{
              display: 'grid',
              gridTemplateColumns: '1fr 100px 100px 100px',
              gap: 4,
              padding: '8px 10px',
              borderBottom: i < activeOverlays.length - 1 ? `1px solid ${C.borderSoft}` : 'none',
              alignItems: 'center',
              opacity: isFailed ? 0.5 : 1,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <span style={{
                  width: 8, height: 8, borderRadius: '50%', background: item.color, flexShrink: 0,
                }} />
                <span style={{
                  fontSize: 11, fontWeight: 700, color: C.white,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{item.label}</span>
                <span style={{ fontSize: 9, color: C.dim3 }}>· {item.ticker}</span>
                {isFailed && <span style={{ fontSize: 9, color: C.red }}>sem dado</span>}
              </div>
              <span style={{
                fontSize: 11, fontWeight: 700,
                color: corr == null ? C.dim3 : (corr >= 0 ? C.green : C.red),
              }}>
                {corr == null ? '—' : `${corr >= 0 ? '+' : ''}${corr.toFixed(3)}`}
              </span>
              <span style={{
                fontSize: 9, fontWeight: 700, letterSpacing: '0.1em',
                color: strength?.color || C.dim3,
              }}>{strength?.label || '—'}</span>
              <span style={{
                fontSize: 11, fontWeight: 700,
                color: ret == null ? C.dim3 : (ret >= 0 ? C.green : C.red),
              }}>
                {ret == null ? '—' : `${ret >= 0 ? '+' : ''}${ret.toFixed(2)}%`}
              </span>
            </div>
          );
        })}
      </div>

      {/* ── ACHIEVEMENT GRID ── */}
      <div style={{ margin: '0 24px', padding: '14px 0', borderTop: `1px solid ${C.border}` }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10,
        }}>
          <span style={{
            fontSize: 9, fontWeight: 700, color: C.dim3, letterSpacing: '0.14em',
          }}>ACHIEVEMENTS · {unlocked.size} / {ACHIEVEMENTS.length} DESBLOQUEADOS</span>
          <div style={{
            flex: 1, height: 3, background: 'rgba(255,255,255,0.05)',
            borderRadius: 2, overflow: 'hidden', maxWidth: 200,
          }}>
            <div style={{
              width: `${(unlocked.size / ACHIEVEMENTS.length) * 100}%`,
              height: '100%', background: `linear-gradient(90deg, ${C.gold}, ${C.purple})`,
              transition: 'width 0.4s',
            }} />
          </div>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 8,
        }}>
          {ACHIEVEMENTS.map(a => {
            const isUnlocked = unlocked.has(a.id);
            return (
              <div key={a.id} style={{
                padding: '8px 10px',
                background: isUnlocked ? 'rgba(212,175,55,0.06)' : 'rgba(255,255,255,0.015)',
                border: `1px solid ${isUnlocked ? 'rgba(212,175,55,0.3)' : C.border}`,
                borderRadius: 5,
                display: 'flex', gap: 8, alignItems: 'flex-start',
                opacity: isUnlocked ? 1 : 0.55,
                transition: 'all 0.2s',
              }}>
                <span style={{
                  fontSize: 16, lineHeight: 1, flexShrink: 0,
                  filter: isUnlocked ? 'none' : 'grayscale(1)',
                }}>{a.icon}</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 10, fontWeight: 700,
                    color: isUnlocked ? C.gold : C.dim2,
                    letterSpacing: '0.03em', marginBottom: 2,
                  }}>{a.name}</div>
                  <div style={{
                    fontFamily: "'Fraunces', Georgia, serif",
                    fontSize: 9.5, color: C.dim1, lineHeight: 1.35,
                    fontStyle: 'italic',
                  }}>{a.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── FOOTER ── */}
      <div style={{
        margin: '0 24px 16px', padding: '8px 12px',
        background: 'rgba(245,200,66,0.05)',
        border: '1px solid rgba(245,200,66,0.1)',
        borderRadius: 4,
      }}>
        <p style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 8.5, color: 'rgba(245,200,66,0.6)',
          lineHeight: 1.45, margin: 0,
        }}>
          <strong style={{ color: C.amber }}>⚠ AVISO CVM:</strong>{' '}
          Ferramenta educacional. Correlações passadas não determinam movimentos futuros.
          Dados: yfinance via indices_etfs :6062 (batch cache, monthly OHLC) · {chartData.length} meses observados.
        </p>
      </div>

      {toast && <AchievementToast achievement={toast} onDismiss={dismissToast} />}
    </div>
  );
};

export default IndexCorrelationLab;